import { Component, OnInit, Inject, Optional } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { OrgContextService } from "src/app/Services/org-context.service";

export interface PublishMembershipOption {
  organizationId: string;
  applicationId: string;
  teamId: string;
  applicationName: string;
  teamName: string;
  label: string;
}

@Component({
  selector: "app-publish-dialog",
  templateUrl: "./publish-dialog.component.html",
  styleUrls: ["./publish-dialog.component.scss"],
})
export class PublishDialogComponent implements OnInit {
  accessType: string;
  membershipOptions: PublishMembershipOption[] = [];
  selectedKey = "";
  requiresScopeSelection = false;

  constructor(
    private orgContext: OrgContextService,
    @Optional() private dialogRef?: MatDialogRef<PublishDialogComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data?: { accessType?: string }
  ) {
    this.accessType = data?.accessType || "";
  }

  ngOnInit(): void {
    this.membershipOptions = this.buildMembershipOptions();
    this.requiresScopeSelection = this.membershipOptions.length > 0;
    if (this.membershipOptions.length === 1) {
      this.selectedKey = this.optionKey(this.membershipOptions[0]);
    } else if (this.membershipOptions.length > 1) {
      const scope = this.orgContext.contentScope();
      const match = this.membershipOptions.find(
        (o) =>
          o.organizationId === scope.organizationId &&
          o.applicationId === (scope.applicationId || "") &&
          o.teamId === (scope.teamId || "")
      );
      this.selectedKey = match
        ? this.optionKey(match)
        : this.optionKey(this.membershipOptions[0]);
    }
  }

  get canPublish(): boolean {
    if (!this.requiresScopeSelection) return true;
    return !!this.selectedKey;
  }

  publishAsPublic(): void {
    this.finish("Public");
  }

  publishAsPrivate(): void {
    this.finish("Private");
  }

  publishAsRestricted(): void {
    this.finish("Restricted");
  }

  private finish(accessType: string): void {
    if (!this.canPublish) return;
    this.applySelectedScope();
    this.accessType = accessType;
    localStorage.setItem("AccessType", this.accessType);
    this.dialogRef?.close(this.accessType);
  }

  private applySelectedScope(): void {
    if (!this.requiresScopeSelection || !this.selectedKey) return;
    const option = this.membershipOptions.find(
      (o) => this.optionKey(o) === this.selectedKey
    );
    if (!option) return;
    try {
      localStorage.setItem("accountUsageMode", "organization");
      localStorage.setItem("activeOrganizationId", option.organizationId);
      localStorage.setItem("activeApplicationId", option.applicationId);
      localStorage.setItem("activeTeamId", option.teamId);
    } catch {
      /* ignore */
    }
    this.orgContext.setActiveApplication(option.applicationId);
    this.orgContext.setActiveTeam(option.teamId);
  }

  private buildMembershipOptions(): PublishMembershipOption[] {
    const snap = this.orgContext.snapshot;
    const orgId = snap.activeOrganization?.organizationId;
    if (!orgId) return [];

    const memberships = (snap.memberships || []).filter(
      (m: any) => m.organizationId === orgId
    );
    // Org-wide manageApplications / manageOrganization → all teams in library.
    const canPickAllTeams = this.orgContext.canManageApplications();
    const options: PublishMembershipOption[] = [];
    const seen = new Set<string>();

    const pushOption = (
      applicationId: string,
      teamId: string,
      applicationName: string,
      teamName: string
    ) => {
      if (!applicationId || !teamId) return;
      const key = `${applicationId}|${teamId}`;
      if (seen.has(key)) return;
      seen.add(key);
      options.push({
        organizationId: orgId,
        applicationId,
        teamId,
        applicationName,
        teamName,
        label: `${applicationName} / ${teamName}`,
      });
    };

    if (canPickAllTeams) {
      for (const team of snap.teams || []) {
        const app = (snap.applications || []).find(
          (a: any) => a.applicationId === team.applicationId
        );
        pushOption(
          team.applicationId,
          team.teamId,
          app?.name || team.applicationName || "Application",
          team.name || "Team"
        );
      }
    } else {
      for (const m of memberships) {
        const applicationId = m.applicationId;
        const teamId = m.teamId;
        if (!applicationId) continue;
        if (teamId) {
          pushOption(
            applicationId,
            teamId,
            m.applicationName || "Application",
            m.teamName || "Team"
          );
        } else {
          // App-level membership: include all teams under that app that user can see
          const teams = (snap.teams || []).filter(
            (t: any) => t.applicationId === applicationId
          );
          for (const team of teams) {
            pushOption(
              applicationId,
              team.teamId,
              m.applicationName || team.applicationName || "Application",
              team.name || "Team"
            );
          }
        }
      }
    }

    return options;
  }

  optionKey(option: PublishMembershipOption): string {
    return `${option.applicationId}|${option.teamId}`;
  }
}
